import { datadogRum } from '@datadog/browser-rum-slim';
import { StatsigClientEventCallback, useStatsigClient } from '@statsig/react-bindings';
import { useSessionValue } from 'appLayer/auth';
import { useEffect } from 'react';
import { useLanguageValue } from '../../state/hooks/useLanguage';
import { useStoreValue } from '../../state/hooks/useStore';
import { useLocale } from './useLocale';

const { version } = require('../../../../package.json');

const dataDogApplicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
const dataDogClientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
const dataDogSite = process.env.NEXT_PUBLIC_DATADOG_SITE;
const dataDogService = process.env.NEXT_PUBLIC_DATADOG_APP_SERVICE;
const isDataDogEnabled =
  dataDogApplicationId && dataDogClientToken && dataDogSite && dataDogService;

let isRumInitialized = false;
const initDdRumClient = () => {
  if (isRumInitialized || !isDataDogEnabled) {
    return;
  }

  datadogRum.init({
    applicationId: dataDogApplicationId,
    clientToken: dataDogClientToken,
    enableExperimentalFeatures: ['feature_flags'],
    site: dataDogSite,
    service: dataDogService,
    sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE) || 100,
    version,
    env: process.env.NEXT_PUBLIC_ENVIRONMENT,
    sessionReplaySampleRate:
      Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE) || 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend(event) {
      if (event.type === 'error') {
        const msg = (event.error?.message || '').toLowerCase();
        const stack = (event.error?.stack || '').toLowerCase();
        const stackTrace = String(event.error?.stackTrace || '').toLowerCase();

        // filter out network errors from third party scripts
        const isThirdPartyFetch =
          msg.includes('failed to fetch') &&
          (stack.includes('ketch.js') ||
            stackTrace.includes('ketch.js') ||
            stack.includes('cp/obtp.js') ||
            stackTrace.includes('cp/obtp.js') ||
            stack.includes('ge.js') ||
            stackTrace.includes('ge.js'));

        if (isThirdPartyFetch) {
          return false;
        }

        // ignore CSP eval blocks (e.g., GTM or extensions)
        const isEvalBlocked = msg.includes('script-src') && msg.includes("'eval' blocked");

        if (isEvalBlocked) {
          return false;
        }
      }

      return true;
    }
  });

  isRumInitialized = true;
};

export const useDataDogOn = () => {
  const store = useStoreValue();
  const lang = useLanguageValue();
  const locale = useLocale();
  const session = useSessionValue();
  const { client } = useStatsigClient();
  const context = client.getContext();

  const initialStableId = context.stableID;
  const { email: customerEmail, id: customerId } = session?.customer ?? {};

  useEffect(() => {
    const currentStableId = client.getContext().stableID;

    const gateEvaluationCallback: StatsigClientEventCallback<'gate_evaluation'> = (event) => {
      if (!event.gate) return;

      const { name, value } = event.gate;
      // Datadog recommends avoiding certain special characters
      // https://docs.datadoghq.com/real_user_monitoring/feature_flag_tracking/setup/?tab=browser#set-up-a-feature-flag-integration
      // Prepend with gate_ to avoid naming conflict with other evaluations
      const formattedKey = `gate_${name.replaceAll('-', '_')}`;
      datadogRum.addFeatureFlagEvaluation(formattedKey, value);
      datadogRum.addAction('gate_evaluation', {
        event,
        stableIdAtEvaluation: currentStableId
      });
    };

    const experimentEvaluationCallback: StatsigClientEventCallback<'experiment_evaluation'> = (
      event
    ) => {
      if (!event.experiment) return;

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { name, __evaluation } = event.experiment;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { is_experiment_active, is_user_in_experiment } = __evaluation || {};

      if (is_experiment_active) {
        // Prepend with exp_ to avoid naming conflict with other evaluations
        const formattedKey = `exp_${name.replaceAll('-', 'tracking_')}`;
        datadogRum.addFeatureFlagEvaluation(formattedKey, is_user_in_experiment);
        datadogRum.addAction('experiment_evaluation', {
          event,
          stableIdAtEvaluation: currentStableId
        });
      }
    };

    client.on('gate_evaluation', gateEvaluationCallback);
    client.on('experiment_evaluation', experimentEvaluationCallback);

    return () => {
      client.off('gate_evaluation', gateEvaluationCallback);
      client.off('experiment_evaluation', experimentEvaluationCallback);
    };
  }, [client]);

  useEffect(() => {
    try {
      if (isDataDogEnabled) {
        initDdRumClient();
        datadogRum.setGlobalContext({
          store,
          lang,
          locale
        });
        datadogRum.setGlobalContextProperty('user', {
          ...(initialStableId && { initialStableId })
        });
        datadogRum.setGlobalContextProperty('customer', {
          ...(customerEmail && { customerEmail }),
          ...(customerId && { customerId })
        });
        datadogRum.startSessionReplayRecording();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
    }
    return () => {
      if (isDataDogEnabled) {
        datadogRum.stopSessionReplayRecording();
      }
    };
  }, [lang, locale, store, customerEmail, customerId, initialStableId]);
};
